import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Copy, Search as SearchIcon } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageHero } from "@/components/public/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const REGENCIES = ["Kota Kendari", "Kabupaten Konawe", "Kabupaten Kolaka", "Kabupaten Muna", "Kabupaten Buton", "Kabupaten Konawe Selatan", "Kabupaten Bombana", "Kabupaten Wakatobi", "Kabupaten Kolaka Utara"];
const TYPES = ["Saran", "Kritik", "Aspirasi", "Pertanyaan", "Masukan pelayanan"];

export default function SuaraMasyarakat() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", regency: "", type: "", subject: "", message: "", attachment: "", attachment_path: "", file: null, consent: false });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const set = (k, v) => { setForm({ ...form, [k]: v }); setErrors({ ...errors, [k]: "" }); };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Nama wajib diisi";
    if (!form.email.trim() || !/^[^@]+@[^@]+\.[^@]+$/.test(form.email)) e.email = "Email tidak valid";
    if (!form.regency) e.regency = "Pilih kabupaten/kota";
    if (!form.type) e.type = "Pilih jenis masukan";
    if (!form.subject.trim()) e.subject = "Subjek wajib diisi";
    if (form.message.trim().length < 10) e.message = "Isi aspirasi minimal 10 karakter";
    if (!form.consent) e.consent = "Anda harus menyetujui pernyataan";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) { toast.error("Mohon lengkapi formulir dengan benar"); return; }
    setSubmitting(true);
    try {
      let attachment = "", attachment_path = "";
      if (form.file) {
        const fd = new FormData();
        fd.append("file", form.file);
        const { data: up } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        attachment = up.filename;
        attachment_path = up.path;
      }
      const { data } = await api.post("/aspirations", { ...form, attachment, attachment_path });
      setResult(data);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mengirim aspirasi");
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <div>
        <PageHero eyebrow="Suara Masyarakat" title="Aspirasi Terkirim" />
        <div className="container-page py-14 max-w-xl">
          <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center shadow-sm" data-testid="aspirasi-success">
            <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-extrabold text-navy">Terima kasih!</h2>
            <p className="text-slate-500 mt-2">Aspirasi Anda telah diterima dan akan segera kami tindaklanjuti.</p>
            <div className="bg-surface rounded-xl p-5 mt-6">
              <div className="text-xs text-slate-500 uppercase tracking-wide">Nomor Tiket Aspirasi</div>
              <div className="flex items-center justify-center gap-2 mt-1">
                <span className="text-2xl font-extrabold text-brandred font-mono" data-testid="ticket-number">{result.ticket}</span>
                <button onClick={() => { navigator.clipboard?.writeText(result.ticket); toast.success("Nomor tiket disalin"); }} className="text-slate-400 hover:text-navy">
                  <Copy className="h-4 w-4" />
                </button>
              </div>
              <div className="mt-3 inline-block text-xs font-bold px-3 py-1 rounded-full bg-blue-100 text-blue-700">Status: {result.status}</div>
            </div>
            <p className="text-xs text-slate-400 mt-4">Simpan nomor tiket untuk memeriksa status aspirasi Anda.</p>
            <div className="flex gap-3 mt-6 justify-center">
              <Link to="/suara-masyarakat/status"><Button className="bg-navy text-white">Cek Status Aspirasi</Button></Link>
              <Button variant="outline" onClick={() => { setResult(null); setForm({ name: "", email: "", phone: "", regency: "", type: "", subject: "", message: "", attachment: "", attachment_path: "", file: null, consent: false }); }}>Kirim Lagi</Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHero eyebrow="Partisipasi Publik" title="Suara Masyarakat"
        description="Ruang bagi masyarakat untuk menyampaikan saran, kritik, aspirasi, dan masukan kepada Bawaslu Provinsi Sulawesi Tenggara." />
      <div className="container-page py-10 grid lg:grid-cols-3 gap-8">
        <form onSubmit={submit} className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 space-y-5" data-testid="aspirasi-form">
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <Label>Nama Lengkap *</Label>
              <Input data-testid="asp-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5" placeholder="Nama Anda" />
              {errors.name && <p className="text-xs text-brandred mt-1">{errors.name}</p>}
            </div>
            <div>
              <Label>Email *</Label>
              <Input data-testid="asp-email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5" placeholder="email@contoh.com" />
              {errors.email && <p className="text-xs text-brandred mt-1">{errors.email}</p>}
            </div>
            <div>
              <Label>Nomor Telepon (opsional)</Label>
              <Input data-testid="asp-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1.5" placeholder="08xx" />
            </div>
            <div>
              <Label>Kabupaten/Kota *</Label>
              <Select value={form.regency} onValueChange={(v) => set("regency", v)}>
                <SelectTrigger className="mt-1.5" data-testid="asp-regency"><SelectValue placeholder="Pilih wilayah" /></SelectTrigger>
                <SelectContent>{REGENCIES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
              {errors.regency && <p className="text-xs text-brandred mt-1">{errors.regency}</p>}
            </div>
          </div>
          <div>
            <Label>Jenis Masukan *</Label>
            <Select value={form.type} onValueChange={(v) => set("type", v)}>
              <SelectTrigger className="mt-1.5" data-testid="asp-type"><SelectValue placeholder="Pilih jenis" /></SelectTrigger>
              <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
            {errors.type && <p className="text-xs text-brandred mt-1">{errors.type}</p>}
          </div>
          <div>
            <Label>Subjek *</Label>
            <Input data-testid="asp-subject" value={form.subject} onChange={(e) => set("subject", e.target.value)} className="mt-1.5" placeholder="Ringkasan singkat" />
            {errors.subject && <p className="text-xs text-brandred mt-1">{errors.subject}</p>}
          </div>
          <div>
            <Label>Isi Aspirasi *</Label>
            <Textarea data-testid="asp-message" value={form.message} onChange={(e) => set("message", e.target.value)} rows={5} className="mt-1.5" placeholder="Tuliskan aspirasi, saran, atau masukan Anda secara jelas..." />
            {errors.message && <p className="text-xs text-brandred mt-1">{errors.message}</p>}
          </div>
          <div>
            <Label>Lampiran (opsional)</Label>
            <Input data-testid="asp-attachment" type="file" accept=".pdf,.jpg,.jpeg,.png" className="mt-1.5" onChange={(e) => { const f = e.target.files?.[0] || null; setForm({ ...form, file: f, attachment: f?.name || "" }); }} />
            <p className="text-xs text-slate-400 mt-1">Format didukung: PDF, JPG, PNG (maks. 5MB). Berkas akan tersimpan aman di server.</p>
            {form.attachment && <p className="text-xs font-semibold text-navy mt-1">Terpilih: {form.attachment}</p>}
          </div>
          <div className="flex items-start gap-3 bg-surface p-4 rounded-lg">
            <Checkbox id="consent" checked={form.consent} onCheckedChange={(v) => set("consent", v)} data-testid="asp-consent" className="mt-0.5" />
            <label htmlFor="consent" className="text-sm text-slate-600 leading-relaxed cursor-pointer">
              Saya menyatakan bahwa informasi yang saya sampaikan benar dan menyetujui data ini digunakan Bawaslu untuk menindaklanjuti aspirasi.
            </label>
          </div>
          {errors.consent && <p className="text-xs text-brandred">{errors.consent}</p>}
          <Button type="submit" disabled={submitting} data-testid="asp-submit" className="w-full bg-brandred hover:bg-brandred-700 text-white h-12 font-semibold text-base">
            {submitting ? "Mengirim…" : "Kirim Aspirasi"}
          </Button>
        </form>

        <div className="space-y-5">
          <div className="bg-navy text-white rounded-2xl p-6">
            <SearchIcon className="h-8 w-8 text-brandred mb-3" />
            <h3 className="font-bold text-lg">Cek Status Aspirasi</h3>
            <p className="text-sm text-slate-300 mt-2">Sudah mengirim aspirasi? Pantau tindak lanjutnya dengan nomor tiket Anda.</p>
            <Link to="/suara-masyarakat/status"><Button className="w-full mt-4 bg-white text-navy hover:bg-slate-100 font-semibold" data-testid="goto-status">Cek Status</Button></Link>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <h3 className="font-bold text-navy mb-3">Alur Tindak Lanjut</h3>
            <div className="space-y-3">
              {["Diterima", "Diverifikasi", "Diproses", "Ditindaklanjuti", "Selesai"].map((s, i) => (
                <div key={s} className="flex items-center gap-3">
                  <span className="h-7 w-7 rounded-full bg-brandred/10 text-brandred text-xs font-bold flex items-center justify-center">{i + 1}</span>
                  <span className="text-sm font-medium text-slate-700">{s}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
